import React, {useState} from 'react';
import {
  Button,
  Alert,
  Content,
  ContentVariants,
  Stack,
  StackItem,
  Modal,
  ModalVariant,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@patternfly/react-core';
import {
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@patternfly/react-icons';
import type {IOAuthApplication} from 'src/resources/OAuthApplicationTypes';

// OAuth scope interface - we'll get this from the JSON endpoint
interface OAuthScope {
  title: string;
  description: string;
  dangerous?: boolean;
}

interface GenerateTokenAuthorizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  application: IOAuthApplication;
  selectedScopes: string[];
  scopesData: Record<string, OAuthScope>;
  hasDangerousScopes?: boolean;
  isAssignmentMode?: boolean;
  targetUsername?: string;
  isPending?: boolean;
}

const GenerateTokenAuthorizationModal: React.FC<
  GenerateTokenAuthorizationModalProps
> = (props): React.ReactElement => {
  const [expandedScopes, setExpandedScopes] = useState<Set<string>>(new Set());

  const toggleScope = (scopeName: string): void => {
    setExpandedScopes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(scopeName)) {
        newSet.delete(scopeName);
      } else {
        newSet.add(scopeName);
      }
      return newSet;
    });
  };

  const isAssignment = props.isAssignmentMode && props.targetUsername;

  return (
    <Modal
      variant={ModalVariant.medium}
      isOpen={props.isOpen}
      onClose={props.isPending ? undefined : props.onClose}
    >
      <ModalHeader
        title={isAssignment ? 'Assign Authorization?' : props.application.name}
      />
      <ModalBody>
        <Stack hasGutter>
          {props.hasDangerousScopes && (
            <StackItem>
              <Alert
                variant="warning"
                title={
                  isAssignment
                    ? `Dangerous scopes will be granted to ${props.targetUsername}. Please ensure the scopes and the user are correct.`
                    : 'This scope grants permissions which are potentially dangerous. Be careful when authorizing it!'
                }
                isInline
              />
            </StackItem>
          )}
          <StackItem>
            <Content component={ContentVariants.p}>
              {isAssignment
                ? `This will prompt user ${props.targetUsername} to generate a token with the following permissions:`
                : 'This application would like permission to:'}
            </Content>
          </StackItem>
          <StackItem>
            <Stack hasGutter>
              {props.selectedScopes.map((scopeName) => {
                const scopeInfo = props.scopesData[scopeName];
                const isDangerous = scopeInfo?.dangerous;
                const isExpanded = expandedScopes.has(scopeName);

                return (
                  <StackItem key={scopeName}>
                    <div>
                      <Button
                        variant="link"
                        isInline
                        onClick={() => toggleScope(scopeName)}
                        icon={
                          isExpanded ? (
                            <ChevronDownIcon />
                          ) : (
                            <ChevronRightIcon />
                          )
                        }
                        iconPosition="left"
                        style={{
                          padding: 0,
                          fontSize: 'inherit',
                          textAlign: 'left',
                        }}
                      >
                        <strong>{scopeInfo?.title}</strong>
                        {isDangerous && (
                          <ExclamationTriangleIcon
                            style={{
                              color: '#f0ab00',
                              marginLeft: '8px',
                              display: 'inline-block',
                            }}
                          />
                        )}
                      </Button>

                      {isExpanded && (
                        <div
                          style={{
                            marginLeft: 'var(--pf-global--spacer--lg)',
                            marginTop: 'var(--pf-global--spacer--xs)',
                            paddingLeft: 'var(--pf-global--spacer--sm)',
                          }}
                        >
                          <Content component={ContentVariants.small}>
                            {scopeInfo?.description}
                          </Content>
                        </div>
                      )}
                    </div>
                  </StackItem>
                );
              })}
            </Stack>
          </StackItem>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button
          key="authorize"
          variant="primary"
          onClick={props.onConfirm}
          isLoading={props.isPending}
          isDisabled={props.isPending}
        >
          {isAssignment ? 'Assign token' : 'Authorize Application'}
        </Button>
        <Button
          key="cancel"
          variant="link"
          onClick={props.onClose}
          isDisabled={props.isPending}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GenerateTokenAuthorizationModal;
